import { showToast } from 'vant';
import {
  ANDROID_QIPAO_PACKAGE,
  buildAndroidIntentActivityWebview,
  buildQipaoActivityWebviewScheme,
} from '../utils/activityWebviewAppLink';
import { isWeChatBrowser } from '../utils/isWeChatBrowser';
import { navigateToDownloadPage } from '../utils/navigateToDownloadPage';
/**
 * 创作者大赛页：「我也要参赛」进 App `/activity-webview?url=...&user_code=...`，含二次确认与深链
 * 依赖宿主组件提供 {@link getBridge}
 */
export default {
  data() {
    return {
      userCode: '',
      showAppLeaveConfirm: false,
      _nativeOpenHandlers: null,
    };
  },
  methods: {
    /**
     * @param {string} key
     * @returns {string}
     */
    getUrlQueryValue(key) {
      if (typeof window === 'undefined') return '';
      const values = [];
      try {
        const sp = new URLSearchParams(window.location.search || '');
        const v = sp.get(key);
        if (v) values.push(v);
      } catch (_) {}
      try {
        const hash = String(window.location.hash || '');
        const idx = hash.indexOf('?');
        if (idx >= 0) {
          const sp2 = new URLSearchParams(hash.slice(idx + 1));
          const v2 = sp2.get(key);
          if (v2) values.push(v2);
        }
      } catch (_) {}
      return values.find(Boolean) || '';
    },
    resolveUserCode() {
      const fromRoute = this.$route?.query?.user_code;
      const fromUrl = this.getUrlQueryValue('user_code');
      const v = (fromRoute || fromUrl || '').toString();
      this.userCode = v.trim();
    },
    /**
     * @returns {boolean}
     */
    isActivityInAppWebView() {
      const bridge = this.getBridge();
      return !!(
        bridge &&
        typeof bridge.isSupported === 'boolean' &&
        bridge.isSupported &&
        typeof bridge.openVideoDetail === 'function'
      );
    },
    getActivityJoinDownloadHref() {
      if (typeof window === 'undefined') return '';
      const u = new URL(window.location.href);
      u.hash = '#/download';
      return u.toString();
    },
    removeActivityJoinListeners() {
      if (typeof document === 'undefined' || !this._nativeOpenHandlers) return;
      const h = this._nativeOpenHandlers;
      if (h.unified) {
        document.removeEventListener('visibilitychange', h.unified);
        window.removeEventListener('pagehide', h.unified);
        window.removeEventListener('blur', h.unified);
      }
      this._nativeOpenHandlers = null;
    },
    /**
     * @param {string} href
     */
    triggerActivityDeeplinkNavigation(href) {
      if (typeof window === 'undefined') return;
      try {
        const a = document.createElement('a');
        a.href = href;
        a.rel = 'nofollow noopener';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (_) {
        try {
          window.location.assign(href);
        } catch (e) {
          window.location.href = href;
        }
      }
    },
    /**
     * @param {string} schemeUrl
     */
    tryActivityQipaoInIframe(schemeUrl) {
      if (typeof document === 'undefined') return;
      const ifr = document.createElement('iframe');
      ifr.setAttribute('aria-hidden', 'true');
      ifr.title = 'open-app';
      ifr.style.cssText = 'position:fixed;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;';
      ifr.src = schemeUrl;
      document.body.appendChild(ifr);
      window.setTimeout(() => {
        try {
          ifr.remove();
        } catch (_) {
          if (ifr.parentNode) ifr.parentNode.removeChild(ifr);
        }
      }, 2000);
    },
    /**
     * 当前活动 H5 完整 URL（含 hash、query）
     * @returns {string}
     */
    getActivityH5FullUrl() {
      if (typeof window === 'undefined' || typeof location === 'undefined') return '';
      return String(location.href || '');
    },
    tryOpenActivityNativeDeepLink() {
      if (typeof window === 'undefined') return;
      const activityUrl = this.getActivityH5FullUrl();
      if (!activityUrl) {
        showToast('无法获取当前页面地址');
        return;
      }

      showToast({ message: '正在打开起泡…', duration: 2000 });
      this.removeActivityJoinListeners();

      const pkg = ANDROID_QIPAO_PACKAGE;
      const isAndroid = /android/i.test(navigator.userAgent || '');
      const fallbackUrl = this.getActivityJoinDownloadHref();
      const androidIntent = buildAndroidIntentActivityWebview(activityUrl, this.userCode, pkg, fallbackUrl);
      const qipaoUrl = buildQipaoActivityWebviewScheme(activityUrl, this.userCode);

      const goDownload = () => {
        navigateToDownloadPage(this.$router);
      };

      const inWeChat = isWeChatBrowser();
      const androidFallbackMs = inWeChat ? 1800 : 2600;
      const iosFallbackMs = inWeChat ? 1500 : 2000;

      let leftPage = false;
      const onLeave = () => {
        if (leftPage) return;
        leftPage = true;
        this.removeActivityJoinListeners();
      };

      const h = (ev) => {
        if (ev && (ev.type === 'pagehide' || ev.type === 'blur')) {
          onLeave();
          return;
        }
        if (document.hidden || document.visibilityState === 'hidden') onLeave();
      };
      this._nativeOpenHandlers = { unified: h };
      document.addEventListener('visibilitychange', h, { passive: true });
      window.addEventListener('pagehide', h, { passive: true });
      window.addEventListener('blur', h, { passive: true });

      if (isAndroid) {
        this.triggerActivityDeeplinkNavigation(androidIntent);
        window.setTimeout(() => {
          this.tryActivityQipaoInIframe(qipaoUrl);
        }, 200);
        window.setTimeout(() => {
          this.removeActivityJoinListeners();
          if (!leftPage) {
            if (!inWeChat) {
              showToast({ message: '未检测到 App，可前往下载页安装', duration: 2500 });
            }
            goDownload();
          }
        }, androidFallbackMs);
        return;
      }

      window.location.assign(qipaoUrl);
      window.setTimeout(() => {
        this.removeActivityJoinListeners();
        if (!leftPage) {
          if (!inWeChat) {
            showToast({ message: '若未自动打开，可前往 App Store 或下载页', duration: 2000 });
          }
          goDownload();
        }
      }, iosFallbackMs);
    },
    /**
     * 「我也要参赛」：先由页面弹二次确认，用户点允许后执行
     */
    /**
     * 仅在外部浏览器经二次确认后调用；App 内嵌走宿主「我也要参赛」原逻辑（发布弹层）
     */
    confirmOpenActivityApp() {
      const activityUrl = this.getActivityH5FullUrl();
      if (!activityUrl) {
        showToast('无法获取当前页面地址');
        return;
      }
      this.tryOpenActivityNativeDeepLink();
    },
  },
  beforeUnmount() {
    this.removeActivityJoinListeners();
  },
};
